import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../apiBase";

const TODAY = new Date().toISOString().slice(0, 10);

export default function InvoiceForm({ onCreated }) {
  const [v, setV] = useState({
    client_name: "", client_email: "", description: "", date: TODAY,
    act_type: "nomenclature", amount_ht: "", tva_rate: "20", sesam_mode: "standard",
  });
  const [busy, setBusy] = useState(false);
  const exon = v.act_type === "nomenclature";
  const ht = parseFloat(v.amount_ht) || 0;
  const tva = exon ? 0 : Math.round(ht * (parseFloat(v.tva_rate) || 0)) / 100 * 1;
  const tvaAmt = exon ? 0 : Math.round(ht * (parseFloat(v.tva_rate) || 0)) / 100;
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!v.client_name.trim() || !v.description.trim() || !(ht > 0)) {
      toast.error("Client, description et montant HT sont requis.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pro/facturation/invoices`, {
        ...v, amount_ht: ht, tva_rate: parseFloat(v.tva_rate) || 0,
      });
      toast.success(`Facture ${r.data.number} créée${r.data.transmitted ? " et transmise au comptable" : ""}.`);
      setV((p) => ({ ...p, client_name: "", client_email: "", description: "", amount_ht: "" }));
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible.");
    } finally {
      setBusy(false);
    }
  };

  const Lbl = ({ children }) => (
    <span className="block text-[10.5px] uppercase tracking-[0.14em] text-[#475569] mb-1">{children}</span>
  );
  const inputCls = "w-full h-9 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[13px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]";

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="invoice-form">
      <div className="text-[13.5px] font-semibold text-[#0F2B5B] mb-4">Nouvelle facture</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block lg:col-span-2"><Lbl>Client / patient</Lbl>
          <input value={v.client_name} onChange={set("client_name")} data-testid="inv-client" className={inputCls} /></label>
        <label className="block"><Lbl>Email client (facultatif)</Lbl>
          <input value={v.client_email} onChange={set("client_email")} data-testid="inv-client-email" className={inputCls} /></label>
        <label className="block"><Lbl>Date</Lbl>
          <input type="date" value={v.date} onChange={set("date")} data-testid="inv-date" className={inputCls} /></label>
        <label className="block lg:col-span-2"><Lbl>Désignation de l'acte</Lbl>
          <input value={v.description} onChange={set("description")} data-testid="inv-description"
                 placeholder="ex : Bilan nutritionnel non conventionné" className={inputCls} /></label>
        <label className="block"><Lbl>Montant HT (€)</Lbl>
          <input type="number" min="0" value={v.amount_ht} onChange={set("amount_ht")} data-testid="inv-amount" className={inputCls} /></label>
        <label className="block"><Lbl>Mode SESAM</Lbl>
          <select value={v.sesam_mode} onChange={set("sesam_mode")} data-testid="inv-sesam" className={inputCls}>
            <option value="standard">SESAM standard</option>
            <option value="sans_vitale">Sans Vitale (avenant 18)</option>
            <option value="degrade">Dégradé (exonéré FSP)</option>
            <option value="">Non applicable</option>
          </select></label>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <button onClick={() => setV((p) => ({ ...p, act_type: "nomenclature" }))} data-testid="inv-type-nomenclature"
                className={`text-left rounded-xl border p-3.5 transition ${exon ? "border-[#0EA5E9] bg-[#F0F9FF]" : "border-[#E2E8F0] bg-white hover:border-[#0EA5E9]"}`}>
          <div className="text-[12.5px] font-semibold text-[#0F2B5B]">Acte de nomenclature — exonéré de TVA</div>
          <div className="text-[11px] text-[#475569] mt-0.5">Art. 261-4-1° CGI · hors périmètre facturation électronique obligatoire</div>
        </button>
        <button onClick={() => setV((p) => ({ ...p, act_type: "hors_nomenclature" }))} data-testid="inv-type-hors"
                className={`text-left rounded-xl border p-3.5 transition ${!exon ? "border-[#0EA5E9] bg-[#F0F9FF]" : "border-[#E2E8F0] bg-white hover:border-[#0EA5E9]"}`}>
          <div className="flex items-center gap-2">
            <div className="text-[12.5px] font-semibold text-[#0F2B5B]">Acte hors nomenclature — soumis à TVA</div>
            {!exon && (
              <select value={v.tva_rate} onChange={set("tva_rate")} data-testid="inv-tva-rate"
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 rounded border border-[#BAE6FD] bg-white text-[11.5px] text-[#0F2B5B] px-1">
                <option value="20">20 %</option><option value="10">10 %</option><option value="5.5">5,5 %</option>
              </select>
            )}
          </div>
          <div className="text-[11px] text-[#475569] mt-0.5">Factur-X (EN 16931) + e-reporting obligatoires</div>
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[13px] text-[#475569]" data-testid="inv-preview-total">
          {exon
            ? <>TTC : <b className="text-[#0F2B5B]">{ht.toFixed(2)} €</b> <span className="text-[11px]">(TVA non applicable)</span></>
            : <>HT {ht.toFixed(2)} € + TVA {tvaAmt.toFixed(2)} € = TTC <b className="text-[#0F2B5B]">{(ht + tvaAmt).toFixed(2)} €</b></>}
        </div>
        <button onClick={submit} disabled={busy} data-testid="inv-submit"
                className="btn-primary h-10 px-6 rounded-full text-[13.5px] font-medium disabled:opacity-50">
          {busy ? "Génération…" : "Émettre la facture Factur-X"}
        </button>
      </div>
    </div>
  );
}
